import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Chip, 
  TextField, 
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { Search, FilterList, Html, CheckCircle, Warning } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useBlogPosts } from '../hooks/useBlogHTML';
import { useAdmin } from '../contexts/AdminContext';
import { BlogFilters, StoryChiefPost } from '../types/blog';

export const BlogList: React.FC = () => {
  const { isAdminMode } = useAdmin();
  
  // Remove search from API filters - we'll handle it client-side
  const [apiFilters, setApiFilters] = useState<BlogFilters>({
    page: 1,
    per_page: 50, // Get more posts to filter client-side
    status: 'published' // Only fetch published posts
  });
  
  // Client-side filter states
  const [searchInput, setSearchInput] = useState('');
  const [selectedKeyword, setSelectedKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;

  const [keywords, setKeywords] = useState<Array<{ value: string; label: string }>>([]);
  const [generatingHTML, setGeneratingHTML] = useState(false);

  const { posts: allPosts, loading, error, htmlStatus, generateHTML, generateAllHTML } = useBlogPosts(apiFilters);

  // Extract unique keywords from posts
  useEffect(() => {
    if (allPosts.length > 0) {
      const uniqueKeywords = new Set<string>();
      
      allPosts.forEach(post => {
        if (post.seo_keyword && post.seo_keyword.trim()) {
          // Split keywords by comma and clean them up
          const postKeywords = post.seo_keyword.split(',').map(k => k.trim());
          postKeywords.forEach(keyword => {
            if (keyword) {
              uniqueKeywords.add(keyword);
            }
          });
        }
      });

      const keywordOptions = Array.from(uniqueKeywords)
        .sort()
        .map(keyword => ({
          value: keyword,
          label: keyword
        }));

      setKeywords(keywordOptions);
    }
  }, [allPosts]);

  // Client-side filtering function
  const getFilteredPosts = (): StoryChiefPost[] => {
    let filtered = [...allPosts];

    // Search filter (searches in title, excerpt, content, and keywords)
    if (searchInput.trim()) {
      const searchTerm = searchInput.toLowerCase().trim();
      filtered = filtered.filter(post => 
        post.title?.toLowerCase().includes(searchTerm) ||
        post.excerpt?.toLowerCase().includes(searchTerm) ||
        post.content?.toLowerCase().includes(searchTerm) ||
        post.seo_keyword?.toLowerCase().includes(searchTerm)
      );
    }

    // Keyword filter
    if (selectedKeyword) {
      filtered = filtered.filter(post => 
        post.seo_keyword?.toLowerCase().includes(selectedKeyword.toLowerCase())
      );
    }

    return filtered;
  };

  // Get paginated posts
  const getPaginatedPosts = (): { posts: StoryChiefPost[], totalPages: number } => {
    const filteredPosts = getFilteredPosts();
    const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
    const startIndex = (currentPage - 1) * postsPerPage;
    const endIndex = startIndex + postsPerPage;
    const paginatedPosts = filteredPosts.slice(startIndex, endIndex);

    return { posts: paginatedPosts, totalPages };
  };

  const { posts, totalPages } = getPaginatedPosts();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleKeywordChange = (value: string) => {
    setSelectedKeyword(value);
    setCurrentPage(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearchInput('');
    setSelectedKeyword('');
    setCurrentPage(1);
  };

  const handleGenerateAllHTML = async () => {
    try {
      setGeneratingHTML(true);
      const results = await generateAllHTML();
      console.log('HTML generation results:', results);
      alert(`Generated HTML files!\nSuccess: ${results.success}\nErrors: ${results.errors}`);
    } catch (error) {
      console.error('Failed to generate HTML files:', error);
      alert('Failed to generate HTML files. Check console for details.');
    } finally {
      setGeneratingHTML(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date unavailable';
    }
  };

  // Helper function to get the image URL from the nested structure
  const getPostImage = (post: StoryChiefPost): string | null => {
    if (post?.featured_image?.data?.url) {
      return post.featured_image.data.url;
    }
    
    if (post?.featured_image?.data?.sizes) {
      const sizes = post.featured_image.data.sizes;
      return sizes.large || sizes.regular || sizes.full;
    }
    
    return null;
  };

  const getImageAlt = (post: StoryChiefPost): string => {
    return post?.featured_image?.data?.alt || 
           post?.featured_image?.data?.name || 
           post?.title || 
           'Blog post image';
  };

  const getPostExcerpt = (post: StoryChiefPost): string => {
    return post?.excerpt || 'No description available';
  };

  // Helper function to get keywords for display
  const getPostKeywords = (post: StoryChiefPost): string[] => {
    if (!post.seo_keyword) return [];
    return post.seo_keyword.split(',').map(k => k.trim()).filter(k => k);
  };

  const htmlFilesCount = Object.values(htmlStatus).filter(Boolean).length;

  if (error && !allPosts.length) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Error Loading Blog</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>{error}</Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
            size="small"
          >
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box textAlign="center" sx={{ mb: 6 }}>
        <Typography 
          variant="h4" 
          component="div"
          sx={{ 
            color: 'primary.main',
            fontWeight: 600,
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
            letterSpacing: 1.5,
            mb: 2,
            textTransform: 'uppercase'
          }}
        >
          Our Blog
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
          Insights, trends, and best practices in recruitment and AI technology
        </Typography>
      </Box>

      {/* Admin Mode: HTML Generation Status - Only show in admin mode */}
      {isAdminMode && (
        <Card sx={{ mb: 4, p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'warning.light' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Html sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">SEO HTML Files (Admin)</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={generatingHTML ? <CircularProgress size={16} /> : <Html />}
              onClick={handleGenerateAllHTML}
              disabled={generatingHTML || loading}
              size="small"
            >
              {generatingHTML ? 'Generating...' : 'Generate All HTML'}
            </Button>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Total Posts: <strong>{allPosts.length}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                HTML Files: <strong>{htmlFilesCount}/{allPosts.length}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Status: <strong>{generatingHTML ? 'Generating...' : 'Ready'}</strong>
              </Typography>
            </Grid>
          </Grid>

          {generatingHTML && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Generating HTML files for SEO...
              </Typography>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            💡 Press Ctrl+Shift+A to toggle admin mode
          </Typography>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 4, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6">Filter Posts</Typography>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search posts..."
              value={searchInput}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Keywords</InputLabel>
              <Select
                value={selectedKeyword}
                label="Keywords"
                onChange={(e) => handleKeywordChange(e.target.value)}
              >
                <MenuItem value="">All Keywords</MenuItem>
                {keywords.map((keyword) => (
                  <MenuItem key={keyword.value} value={keyword.value}>
                    {keyword.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            {(searchInput || selectedKeyword) && (
              <Button 
                fullWidth 
                variant="outlined" 
                onClick={clearFilters}
                sx={{ height: '56px' }}
              >
                Clear Filters
              </Button>
            )}
          </Grid>
        </Grid>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={8}>
          <CircularProgress sx={{ mr: 2 }} />
          <Typography>Loading posts...</Typography>
        </Box>
      ) : posts.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography variant="h5" gutterBottom>No posts found</Typography>
          <Typography color="text.secondary">
            {(searchInput || selectedKeyword) 
              ? 'No results found for your current filters. Try adjusting your search criteria.'
              : 'No posts available at the moment.'}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Show current filter status */}
          {(searchInput || selectedKeyword) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {searchInput && `Searching for: "${searchInput}"`}
                {selectedKeyword && ` • Keyword: ${selectedKeyword}`}
                {` • Found ${getFilteredPosts().length} result${getFilteredPosts().length !== 1 ? 's' : ''}`}
              </Typography>
            </Box>
          )}

          <Grid container spacing={4}>
            {posts.map((post) => (
              <Grid item xs={12} md={6} lg={4} key={post.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  {getPostImage(post) && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={getPostImage(post)!}
                      alt={getImageAlt(post)}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200/e0e0e0/666666?text=No+Image';
                      }}
                      sx={{
                        objectFit: 'cover',
                        backgroundColor: '#f5f5f5'
                      }}
                    />
                  )}
                  
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(post.published_at)}
                      </Typography>
                      
                      {/* Always show cached indicator (green checkmark) */}
                      {htmlStatus[post.slug] && (
                        <Tooltip title="HTML file exists">
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                        </Tooltip>
                      )}
                      
                      {/* Admin Mode: Show warning for missing HTML files */}
                      {isAdminMode && !htmlStatus[post.slug] && (
                        <Tooltip title="No HTML file">
                          <Warning sx={{ fontSize: 16, color: 'warning.main' }} />
                        </Tooltip>
                      )}
                    </Box>
                    
                    <Typography 
                      variant="h6" 
                      component={Link}
                      to={`/blog/${post.slug}`}
                      sx={{ 
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': { color: 'primary.main' },
                        mb: 2,
                        flexGrow: 1
                      }}
                    >
                      {post.title || 'Untitled Post'}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {getPostExcerpt(post)}
                    </Typography>
                    
                    {/* Show keywords */}
                    {getPostKeywords(post).length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {getPostKeywords(post).slice(0, 4).map((keyword, index) => (
                          <Chip 
                            key={index} 
                            label={keyword} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        ))}
                        {getPostKeywords(post).length > 4 && (
                          <Typography variant="caption" color="text.secondary">
                            +{getPostKeywords(post).length - 4} more
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Button 
                        component={Link}
                        to={`/blog/${post.slug}`}
                        variant="text"
                      >
                        Read More →
                      </Button>
                      
                      {/* Admin Mode: Show individual HTML generation button */}
                      {isAdminMode && !htmlStatus[post.slug] && (
                        <Tooltip title="Generate HTML file">
                          <IconButton
                            size="small"
                            onClick={() => generateHTML(post.slug)}
                            color="primary"
                          >
                            <Html fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={6}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* Hidden admin trigger - Triple-click this text to toggle admin mode */}
      <Box sx={{ textAlign: 'center', mt: 8, py: 4 }}>
        <Typography 
          variant="caption" 
          color="text.disabled"
          sx={{ 
            cursor: 'default', 
            userSelect: 'none',
            '&:hover': { color: 'text.secondary' }
          }}
          //data-admin-trigger
          //title="Click to toggle admin mode"
        >
          © 2025 Recruitment Intelligence, LLC. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};